import { Options, Vue } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { SessionStorageEnum } from '@/utils/SessionStorageEnum';
import { walletRefundsService } from '@services/wallet-refunds.service';
import { FilterMatchMode } from 'primevue/api';
import { WalletRefundStatus } from '@/model/WalletRefund';
import moment from 'moment';
import { WalletRefundsRoutesEnum } from '../../router/WalletRefundsRoutesEnum';

import { AppTable, DateFormatted, CurrencyText } from '@sharedComponents';
import { PageHeader } from '@/components';
import RefundDialog from '../../components/RefundDialog/RefundDialog.vue';

interface AdvFilters {
  from_to?: Date[];
}

@Options({
  components: {
    AppTable,
    PageHeader,
    DateFormatted,
    CurrencyText,
    RefundDialog,
  },
  beforeRouteEnter(to, from, next) {
    if (from.name && !(from.name as string).startsWith('wallet-refunds')) {
      sessionStorage.removeItem(SessionStorageEnum.TABLE_STATE_WALLET_REFUNDS);
    }
    next();
  },
  beforeRouteLeave() {
    if (this.service?.cancelPendingRequests) {
      this.service.cancelPendingRequests();
    }
  },
})
export default class WalletRefundsPage extends Vue {
  filters: any = null;
  advFilters: AdvFilters = {
    from_to: undefined,
  };

  showRefundDialog = false;
  refundInEdit: any = null;

  get service() {
    return walletRefundsService;
  }

  get stateKey() {
    return SessionStorageEnum.TABLE_STATE_WALLET_REFUNDS;
  }

  get statusOptions() {
    return [
      { label: 'Richiesto', value: WalletRefundStatus.REQUESTED },
      { label: 'Rimborsato', value: WalletRefundStatus.REFUNDED },
      { label: 'Rifiutato', value: WalletRefundStatus.REJECTED },
    ];
  }

  get table() {
    return this.$refs.dataTable as any;
  }

  formatAmount(amount: any): string {
    if (!amount) return '€ 0,00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(numAmount);
  }

  getStatusLabel(status: WalletRefundStatus): string {
    switch (status) {
      case WalletRefundStatus.REQUESTED:
        return 'Richiesto';
      case WalletRefundStatus.REFUNDED:
        return 'Rimborsato';
      case WalletRefundStatus.REJECTED:
        return 'Rifiutato';
      default:
        return status;
    }
  }

  getStatusSeverity(status: WalletRefundStatus): 'success' | 'info' | 'warning' | 'danger' {
    switch (status) {
      case WalletRefundStatus.REQUESTED:
        return 'warning';
      case WalletRefundStatus.REFUNDED:
        return 'success';
      case WalletRefundStatus.REJECTED:
        return 'danger';
      default:
        return 'info';
    }
  }

  onReset() {
    this.advFilters = {
      from_to: undefined,
    };
    this.updateFilters();
  }

  updateFilters() {
    if (!this.table?.filters) {
      return;
    }

    const createdAtFilter = this.table.filters.created_at;
    if (!createdAtFilter) {
      return;
    }

    const [from, to] = this.advFilters.from_to || [];

    if (from && to) {
      createdAtFilter.value = [
        moment(from).startOf('day').toDate(),
        moment(to).endOf('day').toDate(),
      ];
      createdAtFilter.matchMode = FilterMatchMode.BETWEEN;
    } else if (from) {
      createdAtFilter.value = moment(from).startOf('day').toDate();
      createdAtFilter.matchMode = 'gte';
    } else {
      createdAtFilter.value = null;
      createdAtFilter.matchMode = FilterMatchMode.CONTAINS;
    }

    // Applica i filtri
    this.table.applyFilter();
  }

  onRowClick(event: any) {
    if (event.data && event.data.id) {
      this.$router.push({
        name: WalletRefundsRoutesEnum.WALLET_REFUND_DETAIL,
        params: { id: event.data.id },
      });
    }
  }

  goToDetail(id: number) {
    this.$router.push({
      name: WalletRefundsRoutesEnum.WALLET_REFUND_DETAIL,
      params: { id },
    });
  }

  onRefund(data: any) {
    this.refundInEdit = data;
    this.showRefundDialog = true;
  }

  onRefundSaved() {
    this.showRefundDialog = false;
    this.refundInEdit = null;
    // Aggiorna la lista
    this.table.applyFilter();
  }

  isExporting = false;
  async handleExport() {
    try {
      this.isExporting = true;

      const tableFilters = this.table.filters;
      
      // Costruisce l'oggetto filters solo con i filtri che hanno un valore
      const filters: any = {};
      Object.keys(tableFilters).forEach(key => {
        if (key === 'global') return; // Escludi il filtro globale
        
        const filter = tableFilters[key];
        if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
          filters[key] = {
            value: filter.value,
            matchMode: filter.matchMode
          };
        }
      });

      const params = Object.keys(filters).length > 0 ? { filters } : {};
      const response = await walletRefundsService.exportExcel(params);
      
      const fileName = 'wallet-refunds-export.xlsx';

      // Crea un link temporaneo per il download
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      this.$toast.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Export completato',
        life: 3000,
      });
    } catch (error) {
      console.error('Error exporting:', error);
      this.$toast.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Export non riuscito',
        life: 3000,
      });
    } finally {
      this.isExporting = false;
    }
  }

  private initFilter() {
    this.filters = {
      global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      created_at: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      amount: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      status: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      business_name: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      admin_notes: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    };
  }

  created() {
    this.initFilter();
  }
}
